<template>
  <FilterGroup
    v-if="modelValue"
    :is-collapsible="isCollapsible"
    :badge="modelValue.length"
    :label="filter.name"
    :filter-slug="filter.slug"
    @click:reset="handleReset"
    @click:apply="handleApply"
    @reset-to-initial-state="handleResetToInitialState"
  >
    <MultipleSelectionList
      :model-value="tempModelValue"
      :items="filterValues"
      :use-deep-is-active-check="true"
      :initial-state="modelValue"
      :is-reset="isReset"
      class="grid grid-cols-2 gap-2"
      :class="{ 'm-0': isMobile, 'mx-2 mt-2': !isMobile }"
      name="FilterSelectSize"
      role="listbox"
      @update:model-value="handleModelValueUpdate"
    >
      <template #item="{ item, toggleItem, isActive }">
        <button
          class="flex appearance-none items-center focus:ring-2"
          role="option"
          @click="toggleItem(item)"
        >
          <ColorCircle
            :color="{
              id: getColorId(item),
              value: item.value,
              label: item.displayName,
            }"
            :has-active-dot="isActive"
            class="mr-2"
          />
          {{ item.displayName }}
        </button>
      </template>
    </MultipleSelectionList>
  </FilterGroup>
</template>

<script lang="ts">
import type { ProductFilter } from '@scayle/storefront-nuxt'

import FilterGroup from '~/components/filters/FilterGroup.vue'
import MultipleSelectionList from '#layers/base/components/ui/selectionList/MultipleSelectionList.vue'
import ColorCircle from '~/components/colors/ColorCircle.vue'
import { useDeviceType } from '~/composables/useDeviceType'

export default defineComponent({
  name: 'ColorFilter',
  components: { ColorCircle, MultipleSelectionList, FilterGroup },
  props: {
    modelValue: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    filter: {
      type: Object as PropType<ProductFilter>,
      required: true,
    },
    filterValues: {
      type: Array as PropType<any[]>,
      required: true,
    },
    isCollapsible: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  emits: ['reset', 'apply', 'update:modelValue', 'update:modelValueIds'],
  setup(props, { emit }) {
    const isReset = ref<boolean>(false)
    const { isMobile } = useDeviceType()
    const tempModelValue = ref(props.modelValue)

    const getColorId = (item: any): number => {
      if (typeof item?.id === 'number') {
        return item.id
      }

      const parsedId = Number(item?.id)
      if (Number.isFinite(parsedId)) {
        return parsedId
      }

      const parsedValue = Number(item?.value)
      return Number.isFinite(parsedValue) ? parsedValue : 0
    }

    const handleReset = () => {
      isReset.value = false
      tempModelValue.value = props.modelValue

      nextTick(() => {
        isReset.value = true
        emit('reset')
      })
    }

    const handleResetToInitialState = () => {
      isReset.value = false
      tempModelValue.value = props.modelValue

      nextTick(() => {
        isReset.value = true
      })
    }

    const handleApply = () => {
      isReset.value = false
      emit('update:modelValue', tempModelValue.value)
      emit(
        'update:modelValueIds',
        tempModelValue.value.map((item: any) => item.value),
      )
      emit('apply', {
        values: tempModelValue.value.map((item) => item.value),
        displayNames: tempModelValue.value.map((item) => item.displayName),
      })
    }

    const handleModelValueUpdate = (newValue: any[]) => {
      tempModelValue.value = newValue
      emit('update:modelValue', tempModelValue.value)
      emit(
        'update:modelValueIds',
        tempModelValue.value.map((item: any) => item.value),
      )
    }

    watch(
      () => props.modelValue,
      (newModelValue) => {
        tempModelValue.value = newModelValue
      },
      { deep: true },
    )

    return {
      handleReset,
      handleApply,
      handleResetToInitialState,
      handleModelValueUpdate,
      isMobile,
      isReset,
      tempModelValue,
      getColorId,
    }
  },
})
</script>
